import { ArrayNotEmpty, IsArray, IsNotEmpty, IsOptional, ValidateNested } from "class-validator";
import { AcquisitionModalityModel } from "@/app/acquisition_modalities/models/AcquisitionModalityModel";

export class PreferencesModel {
  @IsOptional()
  public id: number | string;

  @ValidateNested()
  public searchPreferences: SearchPreferences;

  @ValidateNested()
  public reportPreferences: ReportPreferences;

  constructor(data: Partial<PreferencesModel> = {}) {
    this.id = data.id ?? 0;
    this.searchPreferences = data.searchPreferences
      ? new SearchPreferences(data.searchPreferences)
      : new SearchPreferences();
    this.reportPreferences = data.reportPreferences
      ? new ReportPreferences(data.reportPreferences)
      : new ReportPreferences();
  }
}

export class PreferencesDependenciesModel {
  public referenceDates: ReferenceDates[];

  public searchModes: SearchModes[];

  public languages: Languages[];

  public reportStatuses: ReportStatuses[];

  public studyModalities: AcquisitionModalityModel[];

  public searchOrders: SearchOrders[];

  public searchFilters: SearchFilters[];

  public studyViewers: StudyViewers[];

  constructor(data: Partial<PreferencesDependenciesModel> = {}) {
    this.referenceDates = Array.isArray(data.referenceDates)
      ? data.referenceDates.map((item: ReferenceDates) => new ReferenceDates(item))
      : [];
    this.searchModes = Array.isArray(data.searchModes)
      ? data.searchModes.map((item: SearchModes) => new SearchModes(item))
      : [];
    this.languages = Array.isArray(data.languages)
      ? data.languages.map((item: Languages) => new Languages(item))
      : [];
    this.reportStatuses = Array.isArray(data.reportStatuses)
      ? data.reportStatuses.map((item: ReportStatuses) => new ReportStatuses(item))
      : [];
    this.studyModalities = Array.isArray(data.studyModalities)
      ? data.studyModalities.map(
          (item: AcquisitionModalityModel) => new AcquisitionModalityModel(item),
        )
      : [];
    this.searchOrders = Array.isArray(data.searchOrders)
      ? data.searchOrders.map((item: SearchOrders) => new SearchOrders(item))
      : [];
    this.searchFilters = Array.isArray(data.searchFilters)
      ? data.searchFilters.map((item: SearchFilters) => new SearchFilters(item))
      : [];
    this.studyViewers = Array.isArray(data.studyViewers)
      ? data.studyViewers.map((item: StudyViewers) => new StudyViewers(item))
      : [];
  }
}

class PreferenceDependency {
  public id: number;
  public description: string;

  constructor(data: Partial<ReferenceDates> = {}) {
    this.id = data.id ?? 0;
    this.description = data.description ?? "";
  }
}

export class ReferenceDates extends PreferenceDependency {}

export class SearchModes extends PreferenceDependency {}

export class Languages extends PreferenceDependency {}

export class ReportStatuses extends PreferenceDependency {}

export class SearchOrders extends PreferenceDependency {}

export class SearchFilters extends PreferenceDependency {}

export class StudyViewers extends PreferenceDependency {
  public mobile: boolean;
  public web: boolean;

  constructor(data: Partial<StudyViewers> = {}) {
    super(data);
    this.mobile = data.mobile ?? false;
    this.web = data.web ?? false;
  }
}

export class SearchPreferences {
  @IsNotEmpty()
  public defaultFilter: number;

  @IsNotEmpty()
  public defaultSortOrder: number;

  @IsNotEmpty()
  public defaultMode: number;

  @IsNotEmpty()
  public examsPerPage: number;

  @IsNotEmpty()
  public referenceDate: number;

  @IsOptional()
  public onlyExamsRequestedByMe: boolean;

  constructor(data: Partial<SearchPreferences> = {}) {
    this.defaultFilter = data.defaultFilter ?? null;
    this.defaultSortOrder = data.defaultSortOrder ?? null;
    this.defaultMode = data.defaultMode ?? null;
    this.examsPerPage = data.examsPerPage ?? null;
    this.referenceDate = data.referenceDate ?? null;
    this.onlyExamsRequestedByMe = data.onlyExamsRequestedByMe ?? false;
  }
}

export class ReportPreferences {
  @IsNotEmpty()
  public defaultViewer: number;

  @IsArray()
  @ArrayNotEmpty()
  public defaultExamModality: number[];

  @IsArray()
  @ArrayNotEmpty()
  public defaultReportStatus: number[];

  @IsNotEmpty()
  public closeScreenOnFinishReport: boolean;

  @IsNotEmpty()
  public continuousReport: boolean;

  @IsNotEmpty()
  public enableDualScreenReadingMode: boolean;

  @IsOptional()
  public urlViewer: string;

  constructor(data: Partial<ReportPreferences> = {}) {
    this.defaultViewer = data.defaultViewer ?? null;
    this.defaultExamModality = data.defaultExamModality ?? [];
    this.defaultReportStatus = data.defaultReportStatus ?? [];
    this.closeScreenOnFinishReport = data.closeScreenOnFinishReport ?? null;
    this.continuousReport = data.continuousReport ?? null;
    this.enableDualScreenReadingMode = data.enableDualScreenReadingMode ?? null;
    this.urlViewer = data.urlViewer ?? null;
  }
}
